import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Sparkles, Waves, Moon, Wine, Users, Music } from "lucide-react";
import api from "@/lib/api";
import { POOL_NIGHT, NEON_SIGN, SECTION_IMG, GALLERY, SIGNATURE, LOGO } from "@/constants/media";
import { Reveal, WordMark, GoldRule } from "@/components/brand";
import { money } from "@/lib/api";
import WeatherPanel from "@/components/WeatherPanel";

function Hero() {
  const nav = useNavigate();
  return (
    <section className="relative h-screen min-h-[640px] w-full overflow-hidden noise" data-testid="hero">
      <div className="absolute inset-0 reveal-scale">
        <img src={POOL_NIGHT} alt="The D pool at night" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/75 via-ink/45 to-ink" />
        <div className="absolute inset-0 bg-ink/40" />
      </div>
      <div className="relative z-10 h-full flex flex-col items-center justify-center px-6 text-center">
        <img src={LOGO} alt="The D" className="h-32 sm:h-40 md:h-44 w-auto mx-auto mix-blend-screen fade-in drop-shadow-[0_0_40px_rgba(212,175,55,0.4)]" />
        <div className="mt-8 reveal" style={{ animationDelay: ".4s" }}>
          <p className="text-xs sm:text-sm tracking-label uppercase text-pool text-glow-pool">Welcome to The D</p>
          <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl text-foreground mt-4 max-w-3xl leading-tight text-glow-gold">
            The night looks different here.
          </h1>
          <p className="mt-5 text-muted-foreground max-w-lg mx-auto text-sm sm:text-base">
            Your invitation to unwind. A private resort and social house designed for your people, your time, your experience.
          </p>
        </div>
        <div className="mt-10 flex flex-col sm:flex-row gap-4">
          <button onClick={() => nav("/book")} data-testid="hero-reserve-btn" className="btn-gold px-8 py-3.5 text-sm flex items-center gap-2 justify-center">
            Reserve Your Experience <ArrowRight size={16} />
          </button>
          <button onClick={() => nav("/experiences")} data-testid="hero-explore-btn" className="btn-ghost-gold px-8 py-3.5 text-sm">
            Explore The D
          </button>
        </div>
      </div>
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-[10px] tracking-label uppercase text-muted-foreground animate-pulse">Scroll to arrive</div>
    </section>
  );
}

function Feature({ icon: Icon, title, text }) {
  return (
    <div className="glass rounded-lg p-7 hover:glow-gold transition-shadow duration-500">
      <Icon className="text-gold" size={22} />
      <h3 className="font-serif text-2xl mt-4">{title}</h3>
      <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{text}</p>
    </div>
  );
}

export default function Home() {
  const [experiences, setExperiences] = useState([]);
  useEffect(() => { api.get("/experiences").then((r) => setExperiences(r.data.slice(0, 3))).catch(() => {}); }, []);

  return (
    <div>
      <Hero />

      {/* Welcome */}
      <section className="max-w-5xl mx-auto px-6 py-28 text-center">
        <Reveal>
          <p className="text-xs tracking-label uppercase text-gold mb-6">Private by design</p>
          <h2 className="font-serif text-4xl sm:text-5xl leading-tight">An invitation-only escape,<br />hidden in plain sight.</h2>
          <p className="mt-8 text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            The D is not a hotel. It is a private boutique resort and members-only social house — an intimate world of
            electric-blue water, warm light, and quiet luxury, opened only to those we invite.
          </p>
          <div className="mt-10 flex justify-center"><WeatherPanel variant="compact" /></div>
        </Reveal>
      </section>

      {/* Featured pool imagery band */}
      <section className="relative h-[70vh] overflow-hidden noise">
        <div className="absolute inset-0" style={{ backgroundImage: `url(${SECTION_IMG.curtains})`, backgroundSize: "cover", backgroundPosition: "center", backgroundAttachment: "fixed" }} />
        <div className="absolute inset-0 bg-ink/60" />
        <div className="relative z-10 h-full flex items-center justify-center px-6">
          <Reveal className="text-center">
            <Moon className="text-pool mx-auto mb-6" size={28} />
            <h2 className="font-serif text-3xl sm:text-5xl max-w-3xl">Warm light. Cool water. Your people.</h2>
          </Reveal>
        </div>
      </section>

      {/* Stay options */}
      <section className="max-w-7xl mx-auto px-6 py-28">
        <Reveal className="flex items-end justify-between mb-12 flex-wrap gap-4">
          <div>
            <p className="text-xs tracking-label uppercase text-gold mb-3">Stay &amp; Social</p>
            <h2 className="font-serif text-4xl sm:text-5xl">Choose your experience</h2>
          </div>
          <Link to="/experiences" className="btn-ghost-gold px-6 py-2.5 text-sm">View all</Link>
        </Reveal>
        <div className="grid md:grid-cols-3 gap-6">
          {experiences.map((e, i) => (
            <Reveal key={e.id} delay={i * 0.1}>
              <Link to={`/experiences/${e.id}`} className="group block glass rounded-lg overflow-hidden hover:glow-gold transition-shadow duration-500" data-testid={`home-exp-${e.id}`}>
                <div className="h-52 overflow-hidden">
                  <img src={e.image || GALLERY[(i + 2) % GALLERY.length].src} alt={e.title}
                    className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="p-6">
                  <p className="text-[10px] tracking-label uppercase text-pool">{e.category}</p>
                  <h3 className="font-serif text-2xl mt-2">{e.title}</h3>
                  <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{e.short_desc}</p>
                  <div className="mt-4 flex items-center justify-between">
                    <span className="text-gold text-sm">{e.invitation_only ? "By Invitation" : `From ${money(e.base_price)}`}</span>
                    <ArrowRight size={16} className="text-gold group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Signature amenities */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="grid md:grid-cols-3 gap-6">
          <Feature icon={Waves} title="Private Pool & Spa" text="Electric-blue water, candlelit edges, and total privacy — day or night." />
          <Feature icon={Wine} title="The Rosemary Experience" text="Our signature welcome: rosemary cocktails, mocktails, and a chilled scented towel on arrival." />
          <Feature icon={Music} title="Set the Atmosphere" text="Miami Nights, Vegas Lounge, or Poolside Chill — request your soundtrack and lighting theme." />
        </div>
      </section>

      {/* Signature phrase - neon sign */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0 opacity-40" style={{ backgroundImage: `url(${NEON_SIGN})`, backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="absolute inset-0 bg-ink/70" />
        <Reveal className="relative z-10 text-center px-6">
          <Sparkles className="text-gold mx-auto mb-6" size={26} />
          <h2 className="font-serif text-4xl sm:text-6xl italic text-glow-gold text-gold max-w-4xl mx-auto leading-tight">
            {SIGNATURE}
          </h2>
        </Reveal>
      </section>

      {/* CTA */}
      <section className="max-w-4xl mx-auto px-6 py-28 text-center">
        <Reveal>
          <GoldRule className="max-w-xs mx-auto mb-10" />
          <h2 className="font-serif text-4xl sm:text-5xl">Ready for your night at The D?</h2>
          <p className="mt-6 text-muted-foreground">Reservations are reviewed personally by your host. Request your experience or enter your invitation code.</p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/book" className="btn-gold px-8 py-3.5 text-sm">Reserve Your Experience</Link>
            <Link to="/request-invitation" className="btn-ghost-gold px-8 py-3.5 text-sm">Request an Invitation</Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
